import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUserApi,
  getStoreUsersApi,
  addStoreUserApi,
  removeStoreUserApi,
  updateStoreUserApi,
  updateStoreUserPasswordApi,
  getResellerUsersApi,
  addResellerUserApi,
  removeResellerUserApi,
} from "../api/userApi";
import type { RestUpdateStoreUser } from "@KassaroAB/shared/types/StoreUser";
import type { RestUpdateUserPassword } from "@KassaroAB/shared/types/user";
import type { StoreId, ResellerId, UserId, PermissionGroupId } from "@KassaroAB/shared/types/ids";

export const useGetUserQuery = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: () => getUserApi(),
  });
};

export const useGetStoreUsersQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["storeUsers", storeId],
    queryFn: () => getStoreUsersApi(storeId!),
    enabled: !!storeId,
  });
};

export const useAddStoreUserMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: {
      email: string;
      password?: string;
      permissionGroup?: PermissionGroupId;
      chainAccess?: boolean;
    }) => addStoreUserApi(storeId!, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storeUsers", storeId] });
    },
  });
};

export const useUpdateStoreUserMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: UserId;
      data: RestUpdateStoreUser;
    }) => updateStoreUserApi(storeId!, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storeUsers", storeId] });
    },
  });
};

export const useUpdateStoreUserPasswordMutation = (storeId: StoreId | null) => {
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: UserId;
      data: RestUpdateUserPassword;
    }) => updateStoreUserPasswordApi(storeId!, userId, data),
  });
};

export const useRemoveStoreUserMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeStoreUserApi(storeId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storeUsers", storeId] });
    },
  });
};

export const useGetResellerUsersQuery = (resellerId: ResellerId | null) => {
  return useQuery({
    queryKey: ["resellers", resellerId, "users"],
    queryFn: () => getResellerUsersApi(resellerId!),
    enabled: !!resellerId,
  });
};

export const useAddResellerUserMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: { email: string; password?: string }) =>
      addResellerUserApi(resellerId!, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "users"] });
    },
  });
};

export const useRemoveResellerUserMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeResellerUserApi(resellerId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "users"] });
    },
  });
};